import { useEffect, useState } from 'react'
import {
  Receipt, ChevronLeft, Share2, Loader2, CheckCircle2, Clock, XCircle,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { listReceipts } from '../db'
import { shareReceipt } from '../pdf'

/**
 * Receipts history — every sale recorded on this device, newest first.
 *
 * Each row shows date, total, payment method, item summary, and the
 * outbox status (was it confirmed on the server? still queued? rejected?).
 * Tap a row to re-share the PDF receipt via the OS share sheet.
 */
export default function ReceiptsScreen({ onBack }) {
  const [rows, setRows] = useState(null)
  const [sharing, setSharing] = useState(null)

  useEffect(() => {
    listReceipts().then(setRows)
  }, [])

  const handleShare = async (row) => {
    setSharing(row.client_op_id)
    try {
      await shareReceipt(row.pdf_blob, row.client_op_id)
    } catch (e) {
      toast.error('Could not share receipt')
    } finally {
      setSharing(null)
    }
  }

  // Group by day for visual hierarchy.
  const grouped = (rows || []).reduce((acc, r) => {
    const day = new Date(r.occurred_at).toDateString()
    if (!acc[day]) acc[day] = []
    acc[day].push(r)
    return acc
  }, {})
  const dayKeys = Object.keys(grouped)

  // Running total of everything on the device, for the header chip.
  const grandTotal = (rows || []).reduce((s, r) => s + (Number(r.total_amount) || 0), 0)

  return (
    <div className="pc-receipts-shell h-full flex flex-col text-sage-900 overflow-hidden">
      {/* Header */}
      <header
        className="shrink-0 bg-brand-800 text-white"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="pc-receipts-header flex items-center gap-2 px-3 py-2.5">
          <button
            onClick={onBack}
            className="h-11 w-11 -ml-2 grid place-items-center rounded-lg active:bg-white/10 text-brand-100/80"
            aria-label="Back to register"
          >
            <ChevronLeft size={20} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-lg font-semibold text-white leading-tight">Device receipts</h1>
            <p className="text-xs text-brand-100/70">
              {rows == null ? 'Loading…' : `${rows.length} sale${rows.length === 1 ? '' : 's'} recorded on this device`}
            </p>
          </div>
          {rows != null && rows.length > 0 && (
            <div className="text-right shrink-0">
              <p className="text-xs font-bold uppercase tracking-widest text-brand-100/75">Total</p>
              <p className="text-sm font-black text-white tabular-nums">₵{grandTotal.toFixed(2)}</p>
            </div>
          )}
        </div>
      </header>

      <main className="pc-receipts-list flex-1 overflow-y-auto px-3 py-3 min-h-0">
        {rows == null ? (
          <div className="flex items-center justify-center p-12 text-sage-400 gap-2">
            <Loader2 size={18} className="animate-spin" />
            <span className="text-xs">Loading…</span>
          </div>
        ) : rows.length === 0 ? (
          <EmptyState />
        ) : (
          dayKeys.map(day => (
            <div key={day} className="mb-5">
              <p className="text-xs font-bold uppercase tracking-widest text-sage-400 ml-1 mb-2">
                {formatDayHeader(day)}
              </p>
              <div className="pc-receipts-grid space-y-2">
                {grouped[day].map(row => (
                  <button
                    key={row.client_op_id}
                    onClick={() => handleShare(row)}
                    disabled={sharing === row.client_op_id}
                    className="w-full text-left bg-white ring-1 ring-sage-200 rounded-2xl p-3 shadow-sm active:bg-sage-50 disabled:opacity-50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className="text-xs font-mono font-bold text-sage-400 tracking-tight">
                            {row.receipt_ref || `PWA-${String(row.client_op_id).slice(0, 8).toUpperCase()}`}
                          </p>
                          <StatusPill status={row.outbox_status} />
                        </div>
                        <p className="text-xs text-sage-600 truncate">
                          {row.item_summary || `${row.item_count} item${row.item_count === 1 ? '' : 's'}`}
                        </p>
                        <p className="text-xs text-sage-400 mt-0.5">
                          {formatTime(row.occurred_at)} · {row.payment_mode || 'CASH'}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-base font-black text-accent tabular-nums">
                          ₵{Number(row.total_amount).toFixed(2)}
                        </p>
                        <div className="flex items-center gap-1 mt-1 text-xs text-sage-400">
                          {sharing === row.client_op_id
                            ? <Loader2 size={11} className="animate-spin" />
                            : <Share2 size={11} />}
                          Share
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))
        )}
      </main>
    </div>
  )
}

function StatusPill({ status }) {
  if (status === 'SYNCED') {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs font-bold px-1.5 py-0.5 rounded-full bg-brand-50 text-brand-700 ring-1 ring-brand-200 uppercase tracking-tight">
        <CheckCircle2 size={9} /> Synced
      </span>
    )
  }
  if (status === 'QUEUED') {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs font-bold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-200 uppercase tracking-tight">
        <Clock size={9} /> Queued
      </span>
    )
  }
  if (status === 'REJECTED') {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs font-bold px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-600 ring-1 ring-rose-200 uppercase tracking-tight">
        <XCircle size={9} /> Rejected
      </span>
    )
  }
  return null
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center p-12 text-center gap-3">
      <div className="w-16 h-16 rounded-2xl bg-brand-50 ring-1 ring-brand-100 shadow-sm flex items-center justify-center">
        <Receipt size={28} className="text-brand-600" />
      </div>
      <p className="font-display text-lg font-semibold text-brand-800">No receipts yet</p>
      <p className="text-xs text-sage-400 max-w-[14rem]">
        Receipts will appear here after you ring up a sale. They stay on this device
        so you can re-share them later.
      </p>
    </div>
  )
}

function formatDayHeader(dayStr) {
  const day = new Date(dayStr)
  const today = new Date()
  const yesterday = new Date()
  yesterday.setDate(today.getDate() - 1)
  const sameDay = (a, b) => a.toDateString() === b.toDateString()

  if (sameDay(day, today)) return 'Today'
  if (sameDay(day, yesterday)) return 'Yesterday'
  return day.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })
}

function formatTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  } catch { return '' }
}
